import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BrainCircuit, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { leggiTipo } from "@/lib/segmento";
import { Button } from "@/components/ui/button";

const COPY = {
  it: {
    title: "Vuoi che lo ricordi per sempre?",
    body: (dettaglio) =>
      dettaglio
        ? `Hai appena messo ${dettaglio}. Salvo questa nella tua Memoria Operativa così al prossimo cliente è già corretta, senza riscriverla.`
        : "Salvo il tuo modo di lavorare nella Memoria Operativa così al prossimo cliente è già corretto, senza riscriverlo.",
    emailLabel: "La tua email",
    placeholder: "tu@azienda.it",
    yes: "Sì, salva la mia memoria — Gratis 7 giorni",
    no: "No, voglio riscriverlo ogni volta",
    invalid: "Serve un'email valida per salvare la memoria.",
  },
  en: {
    title: "Want me to remember it forever?",
    body: (dettaglio) =>
      dettaglio
        ? `You just added ${dettaglio}. I'll save it in your Operational Memory so it's already right for the next client, without rewriting it.`
        : "I'll save the way you work in your Operational Memory, so it's already right for the next client.",
    emailLabel: "Your email",
    placeholder: "you@company.com",
    yes: "Yes, save my memory — Free for 7 days",
    no: "No, I'd rather rewrite it every time",
    invalid: "A valid email is needed to save your memory.",
  },
};

// Popup dopo il primo preventivo della demo: primo gradino del funnel.
export const MemoryModal = ({ detail, quoteId, onClose }) => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const navigate = useNavigate();
  const [email, setEmail] = useState(localStorage.getItem("op_email") || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const tipo = leggiTipo();

  const invia = (accepted, indirizzo = "") =>
    api
      .post("/public/memory-intent", {
        email: indirizzo,
        tipo,
        detail: detail || "",
        quote_id: quoteId || "",
        accepted,
      })
      .catch(() => {});

  const accetta = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setError(c.invalid);
    setSaving(true);
    localStorage.setItem("op_email", email);
    await invia(true, email);
    navigate("/registrati", { state: { email, tipo } });
  };

  const rifiuta = () => {
    invia(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/70 p-4 backdrop-blur-sm sm:items-center"
      data-testid="memory-modal"
    >
      <form
        onSubmit={accetta}
        className="grain relative w-full max-w-lg overflow-hidden rounded-2xl bg-slate-950 p-8 text-slate-100 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.8)]"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_10%,rgba(79,70,229,0.35),transparent_55%)]" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-300">
            <BrainCircuit className="h-3 w-3" /> Memoria Operativa
          </span>
          <h2 className="mt-5 text-2xl font-extrabold tracking-tight lg:text-3xl">{c.title}</h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-300" data-testid="memory-modal-text">
            {c.body(detail)}
          </p>

          <label className="mt-7 block text-xs font-semibold uppercase tracking-wide text-slate-400">
            {c.emailLabel}
          </label>
          <input
            data-testid="memory-email"
            type="email"
            value={email}
            onChange={(e) => {
              setError("");
              setEmail(e.target.value);
            }}
            placeholder={c.placeholder}
            className="mt-2 h-12 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-indigo-500"
          />
          {error && (
            <p className="mt-2 text-sm font-semibold text-red-400" data-testid="memory-error">
              {error}
            </p>
          )}

          <Button
            data-testid="memory-accept"
            type="submit"
            disabled={saving}
            className="mt-6 w-full gap-2 bg-indigo-600 py-6 text-base text-white hover:bg-indigo-500"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {c.yes}
          </Button>
          <button
            data-testid="memory-decline"
            type="button"
            onClick={rifiuta}
            className="mt-4 w-full text-sm text-slate-400 underline-offset-4 transition-colors hover:text-slate-200 hover:underline"
          >
            {c.no}
          </button>
        </div>
      </form>
    </div>
  );
};
